"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SSITypesBuilder = void 0;
const jwt_decode_1 = __importDefault(require("jwt-decode"));
const utils_1 = require("../utils");
const ObjectUtils_1 = require("../utils/ObjectUtils");
const Internal_types_1 = require("./Internal.types");
class SSITypesBuilder {
    static createInternalPresentationDefinitionV1FromModelEntity(p) {
        const pd = SSITypesBuilder.createCopyAndModifyPresentationDefinition(p);
        return new Internal_types_1.InternalPresentationDefinitionV1(pd.id, pd.input_descriptors, pd.format, pd.name, pd.purpose, pd.submission_requirements);
    }
    static createInternalPresentationDefinitionV2FromModelEntity(p) {
        const pd = SSITypesBuilder.createCopyAndModifyPresentationDefinition(p);
        return new Internal_types_1.InternalPresentationDefinitionV2(pd.id, pd.input_descriptors, pd.format, pd.frame, pd.name, pd.purpose, pd.submission_requirements);
    }
    static createCopyAndModifyPresentationDefinition(p) {
        const pd = JSON.parse(JSON.stringify(p));
        utils_1.JsonPathUtils.changePropertyNameRecursively(pd, '_const', 'const');
        utils_1.JsonPathUtils.changePropertyNameRecursively(pd, '_enum', 'enum');
        utils_1.JsonPathUtils.changeSpecialPathsRecursively(pd);
        return pd;
    }
    static mapExternalVerifiablePresentationToWrappedVP(presentation) {
        const isJwtEncoded = ObjectUtils_1.ObjectUtils.isString(presentation);
        const type = isJwtEncoded
            ? Internal_types_1.VerifiableDataExchangeType.JWT_ENCODED
            : Internal_types_1.VerifiableDataExchangeType.JSONLD;
        let vp = isJwtEncoded ? this.decodeJwtVerifiablePresentation(presentation) : presentation;
        vp = this.isJwtDecodedPresentation(vp)
            ? this.createInternalPresentationFromJwtDecoded(vp)
            : vp;
        const vcs = this.mapExternalVerifiableCredentialsToWrappedVcs(vp.verifiableCredential);
        return {
            type: type,
            original: presentation,
            decoded: isJwtEncoded
                ? (0, jwt_decode_1.default)(presentation)
                : presentation,
            internalPresentation: {
                '@context': vp['@context'],
                type: vp.type,
                holder: vp.holder,
                presentation_submission: vp.presentation_submission,
                verifiableCredential: vcs,
            },
            vcs: vcs,
        };
    }
    static decodeJwtVerifiablePresentation(jwtvp) {
        const externalPresentationJwt = (0, jwt_decode_1.default)(jwtvp);
        return Object.assign(Object.assign({}, externalPresentationJwt.vp), { expirationDate: externalPresentationJwt.exp, holder: externalPresentationJwt.iss, issuanceDate: externalPresentationJwt.nbf, id: externalPresentationJwt.jti });
    }
    static mapExternalVerifiableCredentialsToWrappedVcs(verifiableCredentials) {
        const wrappedVcs = [];
        for (let i = 0; i < verifiableCredentials.length; i++) {
            wrappedVcs.push(this.mapExternalVerifiableCredentialToWrappedVc(verifiableCredentials[i]));
        }
        return wrappedVcs;
    }
    static mapExternalVerifiableCredentialToWrappedVc(verifiableCredential) {
        if (ObjectUtils_1.ObjectUtils.isString(verifiableCredential)) {
            const externalCredentialJwt = (0, jwt_decode_1.default)(verifiableCredential);
            this.createInternalCredentialFromJwtDecoded(externalCredentialJwt);
            return {
                original: verifiableCredential,
                decoded: (0, jwt_decode_1.default)(verifiableCredential),
                type: Internal_types_1.VerifiableDataExchangeType.JWT_ENCODED,
                internalCredential: this.createInternalCredentialFromJwtDecoded(externalCredentialJwt),
            };
        }
        else if (this.isJwtDecodedCredential(verifiableCredential)) {
            return {
                original: verifiableCredential,
                decoded: verifiableCredential,
                type: Internal_types_1.VerifiableDataExchangeType.JWT_DECODED,
                internalCredential: this.createInternalCredentialFromJwtDecoded(verifiableCredential),
            };
        }
        else {
            return {
                original: verifiableCredential,
                decoded: verifiableCredential,
                type: Internal_types_1.VerifiableDataExchangeType.JSONLD,
                internalCredential: verifiableCredential,
            };
        }
    }
    static createInternalCredentialFromJwtDecoded(externalCredentialJwt) {
        var _a, _b;
        const internalCredential = Object.assign({}, externalCredentialJwt.vc);
        if (externalCredentialJwt.exp) {
            const expDate = (_a = internalCredential.credentialSubject) === null || _a === void 0 ? void 0 : _a.expirationDate;
            const jwtExp = parseInt(externalCredentialJwt.exp.toString());
            // fix seconds to millisecs for the date
            const expAsDateStr = jwtExp < 9999999999
                ? new Date(jwtExp * 1000).toISOString().replace(/\.000Z/, 'Z')
                : new Date(jwtExp).toISOString();
            if (expDate && expDate !== expAsDateStr) {
                throw new Error(`Inconsistent expiration dates between JWT claim (${expAsDateStr}) and VC value (${expDate})`);
            }
            internalCredential.credentialSubject.expirationDate = expAsDateStr;
        }
        if (externalCredentialJwt.nbf) {
            const issuanceDate = internalCredential.issuanceDate;
            const jwtNbf = parseInt(externalCredentialJwt.nbf.toString());
            // fix seconds to millisecs for the date
            const nbfAsDateStr = jwtNbf < 9999999999
                ? new Date(jwtNbf * 1000).toISOString().replace(/\.000Z/, 'Z')
                : new Date(jwtNbf).toISOString();
            if (issuanceDate && issuanceDate !== nbfAsDateStr) {
                throw new Error(`Inconsistent issuance dates between JWT claim (${nbfAsDateStr}) and VC value (${issuanceDate})`);
            }
            internalCredential.issuanceDate = nbfAsDateStr;
        }
        if (externalCredentialJwt.iss) {
            const issuer = internalCredential.issuer;
            if (issuer) {
                if (typeof issuer === 'string') {
                    if (issuer !== externalCredentialJwt.iss) {
                        throw new Error(`Inconsistent issuers between JWT claim (${externalCredentialJwt.iss}) and VC value (${issuer})`);
                    }
                }
                else {
                    if (issuer.id !== externalCredentialJwt.iss) {
                        throw new Error(`Inconsistent issuers between JWT claim (${externalCredentialJwt.iss}) and VC value (${issuer.id})`);
                    }
                }
            }
            internalCredential.issuer = externalCredentialJwt.iss;
        }
        if (externalCredentialJwt.sub) {
            const csId = (_b = internalCredential.credentialSubject) === null || _b === void 0 ? void 0 : _b.id;
            if (csId && csId !== externalCredentialJwt.sub) {
                throw new Error(`Inconsistent credential subject ids between JWT claim (${externalCredentialJwt.sub}) and VC value (${csId})`);
            }
            internalCredential.credentialSubject.id = externalCredentialJwt.sub;
        }
        if (externalCredentialJwt.jti) {
            const id = internalCredential.id;
            if (id && id !== externalCredentialJwt.jti) {
                throw new Error(`Inconsistent credential ids between JWT claim (${externalCredentialJwt.jti}) and VC value (${id})`);
            }
            internalCredential.id = externalCredentialJwt.jti;
        }
        return internalCredential;
    }
    static isJwtDecodedCredential(verifiableCredential) {
        return (verifiableCredential['vc'] &&
            verifiableCredential['iss']);
    }
    static isJwtDecodedPresentation(verifiablePresentation) {
        return (verifiablePresentation['vp'] &&
            verifiablePresentation['iss']);
    }
    static createInternalPresentationFromJwtDecoded(jwtVP) {
        const presentation = Object.assign({}, jwtVP.vp);
        if (jwtVP.iss) {
            const holder = presentation.holder;
            if (holder) {
                if (holder !== jwtVP.iss) {
                    throw new Error(`Inconsistent holders between JWT claim (${jwtVP.iss}) and VC value (${holder})`);
                }
            }
            presentation.holder = jwtVP.iss;
        }
        return presentation;
    }
}
exports.SSITypesBuilder = SSITypesBuilder;
